import dayjs from 'dayjs'
import type {
  SameRaceMultiSelection,
  ToteSelection,
  StartingPriceMatchedSelection,
  FobMatchedSelection,
  FobPropositionSelection,
} from '@mobi/betslip/types'
import type { MarketDataTransferObject } from '@mobi/api-types'
import { isRovingBanker } from '@core/Data/Betting/selections'
import { starterToAcceptor } from '@core/Areas/RaceCard/Components/MoreBettingOptions/FixedOddsMarkets/Utils/createBetSelection'
import { FixedOddsMarketDisplayNames } from '@core/Areas/RaceCard/constants'
import type { BetDetails } from '@core/Areas/ShareBet/LoadBet/types'
import { FOB_TICKET_PLACED_BET_TYPE } from '@core/Areas/ShareBet/LoadBet/constants'
import { getSelectionPrice } from '@core/Areas/Racing/Components/SameRaceMulti/SameRaceMultiBettingDrawer/SameRaceMultiPrice'
import { formatFixtureDateToSimpleString } from '@core/Areas/Racing/helpers/formatFixtureDateToSimpleString'
import { getSupportedToteBetTypeFromPlanSeq } from '@core/Areas/ShareBet/LoadBet/isLoadBetSupported'
import type { BuildBetSelectionOptions } from '@core/Areas/ShareBet/LoadBet/buildBetSelection/types'
import { getRaceStartersFromApi } from '@core/Areas/Racing/api'

// Parses a selection string based on '.' and '/' notation
// eg. '1/3.4.6/FD/2.7' will convert to [['1'], ['2','4','6'], ['FD'], ['2','7']]
export const parseSelectionsString = (selectionString: string): string[][] => {
  const selections = selectionString
    .split('/')
    .map(secondaryDivision => secondaryDivision.split('.'))

  return selections
}

export const getToteSelection = async (betDetails: BetDetails) => {
  const { fixtureId, fixtureDate, races, selectionString, planSeq, numberOfCombinations } =
    betDetails

  const singleSelectionGroup = parseSelectionsString(selectionString).length === 1
  const betType = getSupportedToteBetTypeFromPlanSeq(planSeq)

  if (!betType) throw new Error('Unsupported bet type')

  const toteSelection: ToteSelection = {
    fixtureId,
    fixtureDate: dayjs(fixtureDate).format('YYYY-MM-DD'),
    raceNumber: races[0],
    selectionString,
    numberOfCombinations: numberOfCombinations ?? 1,
    isRovingBanker: isRovingBanker(planSeq),
    isAllways: singleSelectionGroup,
    isLegIn: betType === 'Quinella' && !singleSelectionGroup,
    betType,
  }
  return toteSelection
}

export const getFOBSelection = async (
  betDetails: BetDetails,
  opts: BuildBetSelectionOptions = {},
  fixedOddsMarkets?: MarketDataTransferObject[]
): Promise<
  | FobMatchedSelection
  | SameRaceMultiSelection
  | StartingPriceMatchedSelection
  | FobPropositionSelection
> => {
  const {
    fixtureId,
    fixtureDate: fixtureDateAsDate,
    races,
    selectionString,
    srmSelections,
    derivativePropositionSeq,
    betType,
  } = betDetails

  const fixtureDate = formatFixtureDateToSimpleString(fixtureDateAsDate)
  const starters = await getRaceStartersFromApi({ fixtureId, fixtureDate, raceNumber: races[0] })

  // ===============
  // Same Race Multi
  // ===============

  if (srmSelections && srmSelections.length >= 2) {
    const srmSelectionsWithInvalidLegsRemoved = srmSelections.filter(({ acceptorNumber }) => {
      const matchedStarter = starters.find(starter => starter.Number === acceptorNumber)
      if (!matchedStarter) return false
      const { IsScratched, IsSuspended } = matchedStarter.FixedOddsInfo || {}
      if (IsScratched || IsSuspended) return false
      return true
    })
    if (srmSelectionsWithInvalidLegsRemoved.length < 2) throw new Error('Invalid Same Race Multi')

    const selectionString = srmSelectionsWithInvalidLegsRemoved
      .map(s => `selection=${s.acceptorNumber}-${s.legNumber + 1}`)
      .join('&')

    const srmSelectionPrice = await getSelectionPrice(
      fixtureDateAsDate,
      fixtureId,
      races[0],
      selectionString
    )

    const srmSelection: SameRaceMultiSelection = {
      type: 'same-race-multi',
      fixtureId,
      fixtureDate,
      raceNumber: races[0],
      acceptors: srmSelectionsWithInvalidLegsRemoved,
      winPrice: Number(srmSelectionPrice.price),
    }
    return srmSelection
  }

  // ==================
  // Derivative Markets
  // ==================

  const isStartingPrice = betType === FixedOddsMarketDisplayNames.SP

  if (!isStartingPrice && derivativePropositionSeq && fixedOddsMarkets) {
    const matchedMarket = fixedOddsMarkets.find(market =>
      market.Propositions.some(
        proposition => proposition.PropositionSequence === derivativePropositionSeq
      )
    )
    const matchedProposition = matchedMarket?.Propositions.find(
      proposition => proposition.PropositionSequence === derivativePropositionSeq
    )

    if (!matchedMarket || !matchedProposition) throw new Error('Unable to find proposition')

    const derivativeSelection: FobPropositionSelection = {
      type: 'fob-proposition',
      marketCode: matchedMarket.MarketCode,
      selectedAcceptors: [],
      marketName: matchedMarket.MarketName,
      displayName: matchedProposition.Description,
      winPrice: matchedProposition.Price as number,
      propositionSeq: derivativePropositionSeq.toString(),
      winPriceLastSeen: null,
      placePrice: null,
      placePriceLastSeen: null,
      priceSource: 'api',
      multiLegBetType: 'W',
    }
    return derivativeSelection
  }

  const starter = starters.find(starter => starter.Number === parseInt(selectionString))
  if (!starter) throw new Error('Could not match starter')

  // ==============
  // Starting Price
  // ==============

  if (isStartingPrice) {
    const spSelection: StartingPriceMatchedSelection = {
      fixtureId,
      fixtureDate,
      raceNumber: races[0],
      acceptorNumber: parseInt(selectionString, 10),
      marketCode: 'SP',
      marketName: 'Starting Price',
      selectedAcceptor: starterToAcceptor(starter),
    }
    return spSelection
  }

  const { IsScratched, IsSuspended } = starter.FixedOddsInfo || {}
  if (opts.shouldRemoveScratched && (IsScratched || IsSuspended)) {
    throw new Error('Starter is scratched or suspended')
  }

  // =============
  // Fixed Matched
  // =============

  const winPrice = starter.FixedOddsStarterInfo.DisplayWinDividend
  const placePrice = starter.FixedOddsStarterInfo.DisplayPlaceDividend

  const fobMatchedSelection: FobMatchedSelection = {
    type: 'fob-matched',
    fixtureId,
    fixtureDate,
    raceNumber: races[0],
    acceptorNumber: parseInt(selectionString),
    propositionSeq: starter.FixedOddsStarterInfo?.PropositionSequence?.toString(),
    winPrice: Number(winPrice),
    winPriceLastSeen: Number(winPrice),
    placePrice: placePrice !== '-' ? Number(placePrice) : null,
    placePriceLastSeen: placePrice !== '-' ? Number(placePrice) : null,
    priceSource: 'selection',
    multiLegBetType: betDetails.betType === FOB_TICKET_PLACED_BET_TYPE ? 'P' : 'W',
  }
  return fobMatchedSelection
}
